<script setup lang="ts">
import {
  ENTRY_ATTACHMENT_DOCUMENT_EXTENSIONS,
  ENTRY_ATTACHMENT_IMAGE_EXTENSIONS,
} from 'common/constants/file.upload.types';
import { AttachmentContentType } from 'common/enum/entry.attachment.enum';
import { useAttachmentFeature } from '@/composables/attachments/useAttachmentFeature';
import DocumentIcon from '@/assets/icons/DocumentIcon.vue';
import ImageIcon from '@/assets/icons/ImageIcon.vue';
import ComposerChip, { type TComposerChipAppearance } from '@/core/elements/ComposerChip.vue';
import { useTemplateRef } from 'vue';
withDefaults(defineProps<{ disabled?: boolean; appearance?: TComposerChipAppearance }>(), { appearance: 'ghost' });
const emit = defineEmits<{ files: [files: File[]] }>();
const { requestAttachmentAccess } = useAttachmentFeature();
const documentInput = useTemplateRef('documentInput');
const photoInput = useTemplateRef('photoInput');
const openPicker = (type: AttachmentContentType.File | AttachmentContentType.Image): void => {
  if (!requestAttachmentAccess()) return;
  (type === AttachmentContentType.File ? documentInput.value : photoInput.value)?.click();
};
const select = (event: Event): void => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  emit('files', Array.from(input.files ?? []));
  input.value = '';
};
</script>

<template>
  <input
    ref="documentInput"
    type="file"
    multiple
    :accept="ENTRY_ATTACHMENT_DOCUMENT_EXTENSIONS.join(',')"
    class="hidden"
    :disabled="disabled"
    @change="select"
  />
  <input
    ref="photoInput"
    type="file"
    multiple
    :accept="ENTRY_ATTACHMENT_IMAGE_EXTENSIONS.join(',')"
    class="hidden"
    :disabled="disabled"
    @change="select"
  />
  <ComposerChip
    :appearance="appearance"
    :label="$t('entryAttachment.file')"
    :disabled="disabled"
    @click="openPicker(AttachmentContentType.File)"
  >
    <template #icon><DocumentIcon class="size-icon-xs shrink-0" aria-hidden="true" /></template>
  </ComposerChip>
  <ComposerChip
    :appearance="appearance"
    :label="$t('entryAttachment.photo')"
    :disabled="disabled"
    @click="openPicker(AttachmentContentType.Image)"
  >
    <template #icon><ImageIcon class="size-icon-xs shrink-0" /></template>
  </ComposerChip>
</template>
